import { useEffect, useRef, useState } from "react";
import { ghostBtnClass, inputClass, mfgPrimaryBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { uploadBuilderAssistantPhoto } from "@/lib/manufacturing/builderAssistantClient";
import {
  listQcChecklist,
  peekQcChecklist,
  recordQcChecklistCheck,
  saveQcChecklistItem,
  setQcSessionSection,
  type QcChecklistCatalog,
  type QcChecklistCategory,
  type QcChecklistItem,
} from "@/lib/manufacturing/builderAssistantChecklistClient";
import { matchChecklistItem } from "@/lib/manufacturing/qcChecklistImport";

const WALKING = new Set(["active", "tentative", "archive_requested"]);

export function BuilderAssistantQcFailureDialog({
  sessionId,
  observationId,
  description,
  hasPhoto,
  onClose,
  onFiled,
}: {
  sessionId: string;
  observationId: string;
  description: string;
  hasPhoto: boolean;
  onClose: () => void;
  onFiled: () => void;
}) {
  const [categories, setCategories] = useState<QcChecklistCategory[]>([]);
  const [items, setItems] = useState<QcChecklistItem[]>([]);
  const [categoryId, setCategoryId] = useState("");
  const [itemId, setItemId] = useState("");
  const [checklistTitle, setChecklistTitle] = useState("");
  const [title, setTitle] = useState(description.trim());
  const [photo, setPhoto] = useState<File | null>(null);
  const [suggested, setSuggested] = useState(false);
  const [confirmNew, setConfirmNew] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const seeded = useRef(false);

  function applyCatalog(data: QcChecklistCatalog, spoken: string) {
    const liveCategories = data.categories.filter((row) => row.status !== "archived");
    const liveItems = data.items.filter((row) => WALKING.has(row.status));
    setCategories(liveCategories);
    setItems(liveItems);
    if (seeded.current) return;
    seeded.current = true;
    const matchId = matchChecklistItem(spoken, liveItems);
    const match = liveItems.find((row) => row.id === matchId);
    if (match) {
      setCategoryId(match.category_id);
      setItemId(match.id);
      setChecklistTitle(match.title);
      setSuggested(true);
      return;
    }
    setCategoryId(liveCategories.find((row) => row.required)?.id ?? liveCategories[0]?.id ?? "");
  }

  useEffect(() => {
    seeded.current = false;
    const cached = peekQcChecklist();
    if (cached) applyCatalog(cached, description);
    void listQcChecklist().then((res) => {
      if (!res.ok) {
        if (!cached) setError(res.error);
        return;
      }
      applyCatalog(res.data, description);
    });
  }, [description]);

  const inCategory = items.filter((row) => row.category_id === categoryId);
  const categoryName = categories.find((row) => row.id === categoryId)?.name ?? "this";
  const selectedId = inCategory.some((row) => row.id === itemId) ? itemId : "";

  function chooseCategory(nextId: string) {
    setCategoryId(nextId);
    const moved = items.find(
      (row) => row.category_id === nextId && row.title.trim().toLowerCase() === checklistTitle.trim().toLowerCase(),
    );
    setItemId(moved?.id ?? "");
    setConfirmNew(false);
    setSuggested(false);
  }

  async function file(nextItemId: string) {
    setBusy(true);
    setError(null);
    await setQcSessionSection(sessionId, categoryId, true);
    const res = await recordQcChecklistCheck({
      session_id: sessionId,
      item_id: nextItemId,
      result: "fail",
      note: description.trim(),
      observation_id: observationId,
      photo_attached: hasPhoto || Boolean(photo),
    });
    if (!res.ok) {
      setBusy(false);
      setError(res.error);
      return;
    }
    if (photo && res.data.observation_id) {
      const uploaded = await uploadBuilderAssistantPhoto(res.data.observation_id, photo);
      if (!uploaded.ok) {
        setBusy(false);
        setError(uploaded.error);
        return;
      }
    }
    setBusy(false);
    onFiled();
  }

  async function confirm() {
    const trimmed = (checklistTitle.trim() || title.trim());
    if (!categoryId || !title.trim()) {
      setError("Choose a section and a failure.");
      return;
    }
    if (selectedId) {
      await file(selectedId);
      return;
    }
    const existing = inCategory.find((row) => row.title.trim().toLowerCase() === trimmed.toLowerCase());
    if (existing) {
      await file(existing.id);
      return;
    }
    if (!confirmNew) {
      setConfirmNew(true);
      return;
    }
    setBusy(true);
    setError(null);
    const created = await saveQcChecklistItem({
      category_id: categoryId,
      title: trimmed,
      description: title.trim(),
    });
    setBusy(false);
    if (!created.ok) {
      setError(created.error);
      return;
    }
    await file(created.data.id);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center" role="dialog" aria-labelledby="qc-failure-title">
      <form
        className="w-full max-w-md space-y-3 rounded-lg bg-white p-4 shadow-lg dark:bg-zinc-900"
        onSubmit={(event) => {
          event.preventDefault();
          void confirm();
        }}
      >
        <h3 id="qc-failure-title" className="text-base font-semibold text-gray-900 dark:text-zinc-100">
          QC failure
        </h3>
        <p className="text-sm text-gray-700 dark:text-zinc-300">{description}</p>
        {suggested && selectedId ? (
          <p className="text-sm text-gray-600 dark:text-zinc-400">Suggested from your checklist. Change the section or the failure if this is wrong.</p>
        ) : null}
        <label className="block text-xs font-medium text-gray-700 dark:text-zinc-300">
          Section
          <select
            className={`${inputClass} mt-1 w-full`}
            value={categoryId}
            onChange={(event) => chooseCategory(event.target.value)}
          >
            {categories.map((row) => (
              <option key={row.id} value={row.id}>{row.name}</option>
            ))}
          </select>
        </label>
        <label className="block text-xs font-medium text-gray-700 dark:text-zinc-300">
          Failure in this section
          <select
            className={`${inputClass} mt-1 w-full`}
            value={selectedId}
            onChange={(event) => {
              const item = inCategory.find((row) => row.id === event.target.value);
              setItemId(event.target.value);
              if (item) setChecklistTitle(item.title);
              setSuggested(false);
              setConfirmNew(false);
            }}
          >
            <option value="">Type a failure</option>
            {inCategory.map((row) => (
              <option key={row.id} value={row.id}>{row.title}</option>
            ))}
          </select>
        </label>
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <label className={`${ghostBtnClass} inline-flex cursor-pointer`}>
            Failure photo
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              capture="environment"
              className="hidden"
              onChange={(event) => {
                setPhoto(event.target.files?.[0] ?? null);
                event.target.value = "";
              }}
            />
          </label>
          {photo ? <span className="text-sm text-gray-900 dark:text-zinc-100">Photo 1</span> : null}
        </div>
        <label className="block text-xs font-medium text-gray-700 dark:text-zinc-300">
          What you said
          <input
            className={`${inputClass} mt-1 w-full`}
            value={title}
            onChange={(event) => {
              setTitle(event.target.value);
              setSuggested(false);
              setConfirmNew(false);
            }}
          />
        </label>
        {confirmNew ? (
          <p className="text-sm text-amber-900 dark:text-amber-100">
            Are you sure you want to add a new QC checklist item for the {categoryName} section with the title “{(checklistTitle.trim() || title.trim())}”?
          </p>
        ) : null}
        {error ? <p className="text-sm text-red-700 dark:text-red-300">{error}</p> : null}
        <div className="flex flex-wrap gap-2">
          <button type="submit" className={mfgPrimaryBtnClass} disabled={busy || categories.length === 0}>
            {confirmNew ? "Add item and file failure" : "File failure"}
          </button>
          <button type="button" className={ghostBtnClass} disabled={busy} onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
